import { trackAnalyticsEvent } from '@autone/analytics';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

import { buttonVariants, Tooltip } from '../../../atoms';
import { ArrowDownShortIcon } from '../../../icons';
import { cn } from '../../../utils';
import { SIDEBAR_MENU_BUTTON_STYLE_OVERRIDES } from '../constants/sidebarMenuButtonStyleOverrides';
import { useSidebarContext } from '../context/SidebarContext';
import { type SidebarNavigationItemProps } from '../types/Sidebar.types';

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn('flex w-full min-w-0 flex-col gap-1.5', className)}
      {...props}
    />
  );
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn('group/menu-item relative', className)}
      {...props}
    />
  );
}

function SidebarMenuButton({
  asChild = false,
  isActive = false,
  isDisabled = false,
  tooltip,
  className,
  ...props
}: Omit<React.ComponentProps<'button'>, 'ref'> & {
  asChild?: boolean;
  isActive?: boolean;
  isDisabled?: boolean;
  tooltip?: string;
}) {
  const Comp = asChild ? Slot : 'button';
  const { isMobile, state } = useSidebarContext();

  return (
    <Tooltip
      asChild
      content={isMobile || state === 'expanded' ? undefined : tooltip}
      side="right"
    >
      <Comp
        data-testid="sidebar-menu-button"
        data-slot="sidebar-menu-button"
        data-sidebar="menu-button"
        data-active={isActive}
        disabled={isDisabled}
        className={cn(
          buttonVariants({
            variant: 'ghost',
            size: 'default',
          }),
          'peer/menu-button transition-all [&>span:last-child]:truncate',
          'w-full data-[active=true]:bg-sidebar-primary data-[active=true]:hover:bg-sidebar-primary',
          'group-data-[state=collapsed]:[&>span:not(:first-child)]:hidden',
          SIDEBAR_MENU_BUTTON_STYLE_OVERRIDES,
          className,
        )}
        style={{ transitionDuration: 'var(--sidebar-transition-duration)' }}
        {...props}
      />
    </Tooltip>
  );
}

const SidebarNavigationItem: React.FC<SidebarNavigationItemProps> = (props) => {
  const {
    title,
    icon: Icon,
    isActive,
    isDisabled,
    tooltip,
    url,
    onClick,
  } = props;

  const handleAnalyticsClick = () => {
    trackAnalyticsEvent({
      category: 'sidebar',
      action: 'navigation_item_clicked',
      properties: {
        item: title,
        url,
        has_custom_click: !!onClick,
      },
    });
  };

  if (isDisabled) {
    return (
      <SidebarMenuButton
        tooltip={tooltip || title}
        isActive={isActive}
        isDisabled={true}
      >
        {Icon && <Icon />}
        <span>{title}</span>
      </SidebarMenuButton>
    );
  }

  return (
    <SidebarMenuButton
      asChild
      tooltip={tooltip || title}
      isActive={isActive}
      isDisabled={false}
    >
      <a
        href={url}
        // if onClick is provided, prevent default link behavior for client side routing
        onClick={(e) => {
          handleAnalyticsClick();
          if (onClick) {
            e.preventDefault();
            onClick();
          }
        }}
      >
        {Icon && <Icon />}
        <span>{title}</span>
      </a>
    </SidebarMenuButton>
  );
};

function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        'border-sidebar-border ms-6 flex min-w-0 translate-x-px flex-col gap-1 border-l pl-3.5 pr-2.5 py-0.5',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
}

function SidebarMenuSubButton({
  asChild = false,
  size = 'md',
  isActive = false,
  className,
  onClick,
  children,
  ...props
}: Omit<React.ComponentProps<'a'>, 'ref'> & {
  asChild?: boolean;
  size?: 'sm' | 'md';
  isActive?: boolean;
}) {
  const Comp = asChild ? Slot : 'a';

  const handleSubMenuClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      onClick={handleSubMenuClick}
      className={cn(
        'text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:cursor-pointer active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground flex items-center gap-2 overflow-hidden rounded-md p-2 outline-hidden focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0',
        'data-[active=true]:bg-sidebar-primary data-[active=true]:text-sidebar-primary-foreground',
        size === 'sm' && 'text-sm',
        size === 'md' && 'text-md',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );
}

function SidebarMenuArrow({ className }: React.ComponentProps<'svg'>) {
  return (
    <ArrowDownShortIcon
      className={cn(
        'ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180 group-data-[state=collapsed]:hidden',
        className,
      )}
    />
  );
}

export {
  SidebarMenu,
  SidebarMenuArrow,
  SidebarMenuButton,
  SidebarNavigationItem,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
};
